// Recording Frontend - Video capture interface for testers
import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import { trackEvent, trackError } from "./telemetry";

// Environment configuration
// API_URL points to the Upload Server which handles Strapi authentication securely
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
// STRAPI_URL is only used for displaying uploaded media URLs (public access)
const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';
// Note: STRAPI_TOKEN is NO LONGER used client-side - all authenticated calls go through API_URL proxy

function App() {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ player: 0, screen: 0 });
  const [uploadStatus, setUploadStatus] = useState({ player: 'pending', screen: 'pending' });
  const [uploadError, setUploadError] = useState(null);
  const webcamRef = useRef(null);
  const screenRef = useRef(null);
  const webcamRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);

  const [username, setUsername] = useState("");
  const [languageCode, setLanguageCode] = useState("");
  const [game, setGame] = useState("");
  const [gamename, setGamename] = useState("");
  const [testName, setTestName] = useState("");

  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');

  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [xpGameType, setXpGameType] = useState(1);
  const [xpGameScore, setXpGameScore] = useState(1);
  const [comment, setComment] = useState('');
  const [screenRecordingUrl, setScreenRecordingUrl] = useState('');
  const [missingParams, setMissingParams] = useState([]);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // End-to-end correlation id for this recording session. Minted once at
  // startRecording() and propagated to:
  //   - both /upload calls (player + screen) → GCS object custom metadata
  //   - the /api/strapi/videos feedback POST
  // Lets the dashboard / Grafana drill from a single row back to the GCS
  // blob, the Cloud Batch job, and the ML structured logs.
  const recordingSessionIdRef = useRef('');

  // Token-based authentication
  const [accessToken, setAccessToken] = useState('');
  const [tokenError, setTokenError] = useState(null);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null);

  // Check if all required params are present (either via URL params or token)
  const hasRequiredParams = (username && languageCode && game && testName) || accessToken;

  // useEffect(() => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   setUsername(urlParams.get("username") || "");
  //   setLanguageCode(urlParams.get("languageCode") || "");
  //   setGame(urlParams.get("game") || "");
  //   setTestName(urlParams.get("testName") || "");

  //   requestPermissions();
  //   getVideoDevices();
  // }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    // If token is provided, verify it and extract assignment data
    if (token) {
      setAccessToken(token);
      verifyToken(token);
    } else {
      // Fall back to URL params (legacy mode)
      const user = urlParams.get("username") || "";
      const lang = urlParams.get("languageCode") || "";
      const gameParam = urlParams.get("game") || "";
      const test = urlParams.get("testName") || "";

      setUsername(user);
      setLanguageCode(lang);
      setGame(gameParam);
      setGamename(gameParam);
      setTestName(test);

      // Check for missing required parameters
      const missing = [];
      if (!user) missing.push("username");
      if (!lang) missing.push("languageCode");
      if (!gameParam) missing.push("game");
      if (!test) missing.push("testName");
      setMissingParams(missing);
    }

    requestPermissions();
    getInputDevices();
  }, []);

  const verifyToken = async (token) => {
    setIsVerifyingToken(true);
    setTokenError(null);

    try {
      const response = await fetch(`${API_URL}/api/assignments/verify?token=${encodeURIComponent(token)}`);
      const data = await response.json();

      if (!data.valid) {
        setTokenError(data.error || "Invalid or expired access token");
        setIsVerifyingToken(false);
        return;
      }

      // Extract assignment data
      const { assignment } = data;
      setAssignmentData(assignment);
      setUsername(assignment.tester.username);
      setLanguageCode(assignment.tester.language || "en");
      setGame(assignment.test.gamename);
      setGamename(assignment.test.gamename);
      setTestName(assignment.test.testName);

      // Clear any missing params since we got data from token
      setMissingParams([]);

      console.log("Token verified successfully:", assignment);
      trackEvent('tokenVerified', {
        language: assignment?.tester?.language || 'unknown',
        has_assignment: !!assignment,
        game_present: !!assignment?.test?.gamename
      });
    } catch (error) {
      console.error("Token verification error:", error);
      setTokenError("Failed to verify access token. Please try again.");
      trackEvent('tokenFailed', { reason: error?.message || 'unknown' });
      trackError(error, { stage: 'verifyToken' });
    } finally {
      setIsVerifyingToken(false);
    }
  };
  

  useEffect(() => {
    if (isRecording) {
      startRecording();
    } else {
      stopRecording();
    }
  }, [isRecording]);

  const requestPermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      await navigator.mediaDevices.getDisplayMedia({ video: true });
    } catch (error) {
      console.error("Error requesting permissions:", error);
    }
  };


  // const getVideoDevices = async () => {
  //   const devices = await navigator.mediaDevices.enumerateDevices();
  //   const videoDevices = devices.filter(device => device.kind === 'videoinput');
  //   setVideoDevices(videoDevices);
  //   if (videoDevices.length > 0) {
  //     setSelectedVideoDevice(videoDevices[0].deviceId);
  //   }
  // };

  const getInputDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');
    const audioDevices = devices.filter(device => device.kind === 'audioinput');
    
    setVideoDevices(videoDevices);
    setAudioDevices(audioDevices);
    
    if (videoDevices.length > 0) {
      setSelectedVideoDevice(videoDevices[0].deviceId);
    }
    if (audioDevices.length > 0) {
      setSelectedAudioDevice(audioDevices[0].deviceId);
    }
  };

  // Mint a fresh session id every time a recording starts. crypto.randomUUID
  // is available in all browsers that support MediaRecorder + getDisplayMedia
  // (Chrome 92+, Firefox 95+, Safari 15.4+); fall back to a Math.random hex
  // for the long tail so we always have *some* id rather than none.
  const mintSessionId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const rand = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
    return `${rand()}${rand()}-${rand()}-${rand()}`;
  };

  const startRecording = async () => {
    // Reset upload state for new recording
    setUploadProgress({ player: 0, screen: 0 });
    setUploadStatus({ player: 'pending', screen: 'pending' });
    setUploadError(null);
    setShowForm(false);
    recordingSessionIdRef.current = mintSessionId();
    // Expose the rsid to global error handlers so window.error /
    // unhandledrejection can correlate to the active session.
    if (typeof window !== 'undefined') {
      window.__rsid = recordingSessionIdRef.current;
    }
    trackEvent('startRecording', {
      recording_session_id: recordingSessionIdRef.current,
      language: languageCode,
      has_token: !!accessToken
    });

    const webcamStream = await navigator.mediaDevices.getUserMedia({
      video: { 
        deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
        width: 1195, 
        height: 672 
      },
      audio: {
        deviceId: selectedAudioDevice ? { exact: selectedAudioDevice } : undefined
      }      
      // audio: true
    });
    const screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: { width: 1280, height: 720 },
      audio: true
    });

    webcamRef.current.srcObject = webcamStream;
    screenRef.current.srcObject = screenStream;

    webcamRecorderRef.current = new MediaRecorder(webcamStream);
    screenRecorderRef.current = new MediaRecorder(screenStream);

    const webcamChunks = [];
    const screenChunks = [];

    webcamRecorderRef.current.ondataavailable = (e) => webcamChunks.push(e.data);
    screenRecorderRef.current.ondataavailable = (e) => screenChunks.push(e.data);

    webcamRecorderRef.current.onstop = () => uploadVideo(webcamChunks, 'player');
    screenRecorderRef.current.onstop = () => uploadVideo(screenChunks, 'screen');

    webcamRecorderRef.current.start();
    screenRecorderRef.current.start();
  };

  const stopRecording = () => {
    webcamRecorderRef.current?.stop();
    screenRecorderRef.current?.stop();
    trackEvent('stopRecording', {
      recording_session_id: recordingSessionIdRef.current
    });
  };

  const getStatusText = (status, progress) => {
    switch (status) {
      case 'pending':
        return 'Waiting...';
      case 'uploading':
        return `${progress}%`;
      case 'uploading_strapi':
        return 'Saving to dashboard...';
      case 'success':
        return 'API upload complete';
      case 'complete':
        return '✓ Complete';
      case 'failed':
        return '✗ Failed';
      case 'strapi_failed':
        return '⚠ Dashboard upload failed';
      default:
        return status;
    }
  };

  const uploadVideo = async (chunks, type) => {
    const blob = new Blob(chunks, { type: 'video/mp4' });
    const formData = new FormData();
    formData.append('video', blob, `${type}.mp4`);
    formData.append('type', type);
    formData.append('jugador', username);
    formData.append('game', game);
    formData.append('testOptionalID', testName);
    formData.append('languageCode', languageCode);

    // Include access token if available (for assignment tracking)
    if (accessToken) {
      formData.append('accessToken', accessToken);
    }

    // Stamp both player and screen uploads with the same session id so they
    // land in GCS with matching custom metadata and the Cloud Function can
    // forward it to Cloud Batch + ML upsert.
    if (recordingSessionIdRef.current) {
      formData.append('recordingSessionId', recordingSessionIdRef.current);
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadStatus(prev => ({ ...prev, [type]: 'uploading' }));
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));

    const uploadStartedAt = Date.now();
    trackEvent('uploadStarted', {
      type,
      size_bytes: blob.size,
      recording_session_id: recordingSessionIdRef.current
    });

    try {
      // Use XMLHttpRequest for proper upload progress tracking
      const apiResponse = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            setUploadProgress(prev => ({ ...prev, [type]: percentCompleted }));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              resolve({ success: true });
            }
          } else {
            try {
              reject(JSON.parse(xhr.responseText));
            } catch {
              reject({ error: `Upload failed with status ${xhr.status}` });
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject({ error: 'Network error during upload' });
        });

        xhr.addEventListener('timeout', () => {
          reject({ error: 'Upload timed out' });
        });

        xhr.open('POST', `${API_URL}/upload`);
        xhr.timeout = 300000; // 5 minute timeout for large videos
        xhr.send(formData);
      });

      console.log(`${type} video uploaded to API:`, apiResponse);
      setUploadStatus(prev => ({ ...prev, [type]: 'success' }));
      trackEvent('uploadComplete', {
        type,
        size_bytes: blob.size,
        duration_ms: Date.now() - uploadStartedAt,
        recording_session_id: recordingSessionIdRef.current
      });

      // For screen recordings, also upload to Strapi via Upload Server proxy
      if (type === 'screen') {
        setUploadStatus(prev => ({ ...prev, [type]: 'uploading_strapi' }));

        const strapiFormData = new FormData();
        strapiFormData.append('file', blob, `${username}_screen.mp4`);

        try {
          // Use Upload Server proxy for secure Strapi upload (token handled server-side)
          const strapiUploadResponse = await fetch(`${API_URL}/api/strapi/upload`, {
            method: 'POST',
            body: strapiFormData
          });

          if (strapiUploadResponse.ok) {
            const uploadData = await strapiUploadResponse.json();
            const strapiVideoUrl = `${STRAPI_URL}${uploadData[0].url}`;
            setScreenRecordingUrl(strapiVideoUrl);
            setUploadStatus(prev => ({ ...prev, [type]: 'complete' }));
            setShowForm(true);
          } else {
            const errorData = await strapiUploadResponse.json().catch(() => ({}));
            console.error('Strapi upload failed:', errorData);
            setUploadStatus(prev => ({ ...prev, [type]: 'strapi_failed' }));
          }
        } catch (strapiError) {
          console.error('Strapi upload error:', strapiError);
          setUploadStatus(prev => ({ ...prev, [type]: 'strapi_failed' }));
        }
      } else {
        setUploadStatus(prev => ({ ...prev, [type]: 'complete' }));
      }

    } catch (error) {
      console.error(`Error uploading ${type} video:`, error);
      setUploadStatus(prev => ({ ...prev, [type]: 'failed' }));
      setUploadError(error.error || error.message || `Failed to upload ${type} video`);
      trackEvent('uploadFailed', {
        type,
        size_bytes: blob.size,
        duration_ms: Date.now() - uploadStartedAt,
        reason: error?.error || error?.message || 'unknown',
        recording_session_id: recordingSessionIdRef.current
      });
      trackError(error instanceof Error ? error : new Error(error?.error || 'upload failed'), {
        stage: 'uploadVideo',
        type,
        recording_session_id: recordingSessionIdRef.current
      });
    } finally {
      // Only set isUploading to false when both uploads are done
      const otherType = type === 'player' ? 'screen' : 'player';
      setUploadStatus(prev => {
        const allDone = ['complete', 'failed', 'strapi_failed'].includes(prev[otherType]);
        if (allDone) {
          setIsUploading(false);
        }
        return prev;
      });
    }
  };

  const handleSubmitForm = async () => {
    if (isSubmittingForm) return;
    setIsSubmittingForm(true);
    setSubmitError(null);
    try {
      // Use Upload Server proxy for secure Strapi API call (token handled server-side)
      const response = await fetch(`${API_URL}/api/strapi/videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: {
            xpGameType,
            xpGameScore,
            comment,
            username,
            gamename,
            testName,
            // languageCode is NOT on the Video content-type — omit it so
            // Strapi never rejects the feedback payload as an unknown field.
            screenRecording: screenRecordingUrl,
            // Same session id as the GCS uploads — lets the ML upsert
            // collapse the feedback row + the inference row onto a single
            // canonical record instead of producing the V2-P6 dual rows.
            recordingSessionId: recordingSessionIdRef.current || undefined
          }
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody.error || errorBody.message || `Submission failed (HTTP ${response.status})`
        );
      }

      console.log('Form data submitted successfully');
      trackEvent('feedbackSubmitted', {
        xp_game_type: xpGameType,
        xp_game_score: xpGameScore,
        comment_length: comment ? comment.length : 0,
        recording_session_id: recordingSessionIdRef.current
      });
      setSubmitSuccess(true);
      // Notify the parent dashboard (RecorderOverlay iframe host) that the
      // test is complete so it can close the overlay.
      try {
        window.parent.postMessage({ type: 'recorder:submitted' }, '*');
      } catch (postError) {
        console.warn('Failed to notify parent window:', postError);
      }
      setTimeout(() => {
        setShowForm(false);
        setSubmitSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'Failed to submit feedback. Please try again.');
      trackError(error instanceof Error ? error : new Error(String(error)), {
        stage: 'handleSubmitForm',
        recording_session_id: recordingSessionIdRef.current
      });
    } finally {
      setIsSubmittingForm(false);
    }
  };
  

  return (
    <div className="App">
      <header className="App-header">
        <img className="logo" src="/logo.png"></img>
      </header>
      {showForm && (
        <div className="feedback-form">
          <h2>Test Feedback</h2>
          <div className="form-group">
            <label>XP Game Type (1-5):</label>
            <input
              type="number"
              min="1"
              max="5"
              value={xpGameType}
              onChange={(e) => setXpGameType(Math.min(5, Math.max(1, parseInt(e.target.value))))}
            />
          </div>
          <div className="form-group">
            <label>XP Game Score (1-10):</label>
            <input
              type="number"
              min="1"
              max="10"
              value={xpGameScore}
              onChange={(e) => setXpGameScore(Math.min(10, Math.max(1, parseInt(e.target.value))))}
            />
          </div>
          <div className="form-group">
            <label>Comment:</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="4"
            />
          </div>
          {submitError && (
            <div className="form-error" role="alert" style={{ color: '#ff6b6b', marginBottom: '0.5rem' }}>
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className="form-success" role="status" style={{ color: '#51cf66', marginBottom: '0.5rem' }}>
              Feedback submitted. Closing…
            </div>
          )}
          <button onClick={handleSubmitForm} disabled={isSubmittingForm || submitSuccess}>
            {isSubmittingForm ? 'Submitting…' : 'Submit Feedback'}
          </button>
        </div>
      )}
      <main className="App-main">
        <div className="user-info">
          <p><span>Username:</span> {username}</p>
          <p><span>Language Code:</span> {languageCode}</p>
          <p><span>Game:</span> {game}</p>
          <p><span>Test Name:</span> {testName}</p>
        </div>
        <div className="input-devices-selection">
          <div className="webcam-selection">
            <label htmlFor="webcam-select">Choose webcam: </label>
            <select 
              id="webcam-select" 
              value={selectedVideoDevice} 
              onChange={(e) => setSelectedVideoDevice(e.target.value)}
            >
              {videoDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="microphone-selection">
            <label htmlFor="microphone-select">Choose microphone: </label>
            <select 
              id="microphone-select" 
              value={selectedAudioDevice} 
              onChange={(e) => setSelectedAudioDevice(e.target.value)}
            >
              {audioDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isVerifyingToken && (
          <div style={{
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#1565c0'
          }}>
            <strong>Verifying access token...</strong>
          </div>
        )}
        {tokenError && (
          <div style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#c62828'
          }}>
            <strong>Access Error:</strong> {tokenError}
            <br />
            <small>
              Please use the link from your tester panel to access tests.
            </small>
          </div>
        )}
        {assignmentData && (
          <div style={{
            backgroundColor: '#e8f5e9',
            border: '1px solid #4caf50',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#2e7d32'
          }}>
            <strong>✓ Authenticated via secure token</strong>
            <br />
            <small>
              Test expires: {new Date(assignmentData.tokenExpiresAt).toLocaleString()}
            </small>
          </div>
        )}
        {missingParams.length > 0 && !accessToken && (
          <div style={{
            backgroundColor: '#fff3e0',
            border: '1px solid #ff9800',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#e65100'
          }}>
            <strong>Missing required URL parameters:</strong> {missingParams.join(', ')}
            <br />
            <small>
              URL format: ?token=ACCESS_TOKEN (recommended)
              <br />
              Or legacy: ?username=NAME&languageCode=en&game=GAME&testName=TEST
            </small>
          </div>
        )}
        <button
          className={`record-button ${isRecording ? 'recording' : ''}`}
          onClick={() => setIsRecording(!isRecording)}
          disabled={isUploading || !hasRequiredParams || isVerifyingToken || !!tokenError}
        >
          <span className="button-text">{isRecording ? 'Stop Recording' : 'Start Recording'}</span>
          <span className="button-icon">{isRecording ? '⏹' : '⏺'}</span>
        </button>
        {isRecording && (
          <div className="preview-container">
            <div className="webcam-container">
              <video ref={webcamRef} autoPlay muted className="webcam-video" width="1195" height="672" />
            </div>
            <video ref={screenRef} autoPlay muted className="preview-video" width="1280" height="720" />
          </div>
        )}
        {isUploading && (
          <div className="upload-status" style={{
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h3 style={{ margin: '0 0 16px' }}>Uploading Videos...</h3>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>Player Video</span>
                <span>{getStatusText(uploadStatus.player, uploadProgress.player)}</span>
              </div>
              <progress value={uploadProgress.player} max="100" style={{ width: '100%', height: '20px' }}></progress>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>Screen Recording</span>
                <span>{getStatusText(uploadStatus.screen, uploadProgress.screen)}</span>
              </div>
              <progress value={uploadProgress.screen} max="100" style={{ width: '100%', height: '20px' }}></progress>
            </div>

            {uploadError && (
              <div style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                padding: '12px',
                color: '#c62828',
                marginTop: '12px'
              }}>
                <strong>Error:</strong> {uploadError}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
